'use client'
import { RadioGroup, Button, cn, Link, useDisclosure, Popover, Image, PopoverContent, PopoverTrigger, Modal, ModalBody, ModalContent, ModalHeader, ModalProps } from "@heroui/react"
import { useForm, Controller } from "react-hook-form"
import { PricingRadio } from "./PricingRadio"
import { useEffect, useMemo, useRef } from "react"
import { useMutation } from "@tanstack/react-query"
import { payssionSubscribe, subscribe, type PaypalSubscribeData } from "@/apis/pricing"
import { useToast } from "@/provider/ToastProvider"
import { Confirm } from "@/ui/Confirm"
import { DarkButton, LightButton } from "@/ui/Button"
import { useLocale, useTranslations } from "next-intl"
import useGoodsList from "./useGoodsList"
import PayMethodModal from "./PayMethodModal"
import UpgradeMembershipModal from "./UpgradeMembershipModal"
import { markPendingPremiumSubscribe } from "./pendingSubscribe"
import { savePayRedirect } from "@/util/payRedirect"
import RedeemEntry from "./RedeemEntry"

export default ({ goods, className }: { goods: any; className?: string }) => {

  const t = useTranslations()
  const locale = useLocale()

  const products = useGoodsList(goods)

  const methods = useForm<{ goodsId: number }>()

  useEffect(() => {
    methods.reset({ goodsId: products[0].id })
  }, [products])

  const { showToast } = useToast()
  const confirmModal = useDisclosure()
  const upgradeModal = useDisclosure()
  const payMethodModal = useDisclosure()

  const url = useRef<string>(null!)
  const goodsId = useRef<number>(null!)
  const upgradeConfirm = useRef<string>("")

  const handleSubscribeRes = (res: { status: number; data?: PaypalSubscribeData; message?: string; msg?: string }) => {
    if (res.status !== 200) {
      showToast({ type: "error", message: res.message })
      return
    }

    switch (Number(res.data?.status)) {
      case 200:
        upgradeModal.onClose()
        if (res.data?.link) {
          markPendingPremiumSubscribe(Number(goodsId.current))
          window.open(res.data.link, "_self")
        }
        break
      case 204:
        upgradeModal.onClose()
        url.current = res.data?.link || ""
        confirmModal.onOpen()
        break
      case 209:
        upgradeModal.onClose()
        showToast({ message: t('pricing.planAlreadyActive') })
        break
      case 210:
        upgradeModal.onClose()
        showToast({ message: t('pricing.planIncludedInCurrent') })
        break
      case 211:
        payMethodModal.onClose()
        upgradeConfirm.current = res.data?.upgrade_confirm || ""
        upgradeModal.onOpen()
        break
      case 0:
        upgradeModal.onClose()
        showToast({ type: "error", message: t('pricing.paypalNotConfigured') })
        break
      default:
        showToast({ type: "error", message: res.message || res.msg })
    }
  }

  const paypalSubscribeMuta = useMutation({
    mutationFn: subscribe,
    onSuccess: handleSubscribeRes,
  })

  const requestSubscribe = (upgradeConfirmToken?: string) => {
    paypalSubscribeMuta.mutate({
      goods_id: Number(goodsId.current),
      ...(upgradeConfirmToken ? { upgrade_confirm: upgradeConfirmToken } : {}),
    })
  }

  // 提交
  const onSubmit = (val) => {
    savePayRedirect()
    goodsId.current = Number(val.goodsId)
    if (locale === 'ru') {
      payMethodModal.onOpen()
      return
    }
    requestSubscribe()
  }

  const onPressPaypal = () => {
    savePayRedirect()
    requestSubscribe()
  }

  const onConfirmUpgrade = () => {
    requestSubscribe(upgradeConfirm.current)
  }

  // 俄罗斯支付
  const payssionSubscribeMuta = useMutation({
    mutationFn: payssionSubscribe,
    onSuccess(res) {
      if (res.status === 200) {
        markPendingPremiumSubscribe(Number(goodsId.current), "payssion")
        window.open(res.data.link, "_self")
      } else {
        showToast({ type: "error", message: res.message })
      }
    }
  })

  const onPressPayssion = () => {
    savePayRedirect()
    payssionSubscribeMuta.mutate({ goods_id: goodsId.current })
  }


  const onClickOpen = () => {
    if (url.current) {
      savePayRedirect()
      markPendingPremiumSubscribe(Number(goodsId.current))
      window.open(url.current, "_self")
      url.current = null!
      confirmModal.onClose()
    }
  }



  return (
    <>
      <form className={cn("px-2", className)} onSubmit={methods.handleSubmit(onSubmit)}>

        <div className="w-full pt-4 lg:mt-8 overflow-auto lg:overflow-visible p-2 pr-4 lg:pr-0">

          <Controller
            control={methods.control}
            name="goodsId"
            render={({ field }) => (
              <RadioGroup
                classNames={{
                  base: "w-fit sm:w-full items-center",
                  wrapper: "flex-nowrap justify-normal sm:justify-center lg:justify-normal gap-2 lg:gap-5 w-fit sm:w-full"
                }}
                value={field.value}
                onValueChange={field.onChange}
                orientation="horizontal"
              >
                {products.map(item => (
                  <PricingRadio key={item.id} value={item.id} time={item.title} pricing={item.pricing} rebate={item.rebate} unit={item.unit} desc={item.desc} />
                ))}

              </RadioGroup>
            )}
          />
        </div>



        <div className="pr-4 sm:pr-0">
          <div className="mt-[20px] text-[12px] text-theme-primary/40 leading-3 sm:leading-[20px] flex flex-col gap-1">
            {
              goods.term.map(item => (
                <p key={item}>- {item}</p>
              ))
            }
          </div>

          <p className="my-2.5 text-small opacity-70">{t('pricing.cancelBeforeSwitchPlan')}</p>
          <Button className={cn(
            "h-[48px] text-theme-white font-bold text-[20px]",
            "bg-gradient-to-l from-gradient-purple2 to-gradient-blue2"
          )}
            type="submit"
            fullWidth
            radius="full"
            isLoading={paypalSubscribeMuta.isPending && !upgradeModal.isOpen}
          >
            {t('pricing.subscribeNow')}
          </Button>

          <RedeemEntry className="mt-4" />

          <div className="flex justify-center gap-6 mt-[24px] flex-wrap">
            {locale === 'ru' &&
              <Popover placement="top">
                <PopoverTrigger>
                  <Button
                    disableRipple
                    className="text-theme-primary/70 h-auto cursor-pointer text-[12px] py-0 px-2 !bg-transparent underline underline-offset-4 overflow-visible"
                    variant="light">Как отменить подписку</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <h2 className="font-bold text-[16px] mb-2">Как отменить подписку?</h2>
                    <p className="text-theme-primary/70 leading-6">1.Найдите раздел «Управление подписками» в верхней части страницы.</p>
                    <p className="text-theme-primary/70 leading-6">2.Нажмите «Управление подписками», чтобы перейти на страницу «Мои подписки».</p>
                    <p className="text-theme-primary/70 leading-6">3.Найдите товар, на который оформлена подписка, и нажмите «Отменить подписку».</p>
                    <p className="text-theme-primary/70 leading-6">4.Подтвердите отмену подписки, чтобы завершить процесс.</p>
                  </div>
                </PopoverContent>
              </Popover>
            }
            <Link underline="always" href="https://hiwaifu.com/privacy.html" className="text-theme-primary/70 cursor-pointer text-[12px]">{t('pricing.privacyPolicy')}</Link>
            <Link underline="always" href="https://hiwaifu.com/terms.html" className="text-theme-primary/70 cursor-pointer text-[12px]">{t('pricing.termsOfUse')}</Link>
          </div>
        </div>

      </form>

      <Confirm
        message="You have a pending order. Please proceed to pay or cancel the payment."
        title=""
        isOpen={confirmModal.isOpen}
        onOpenChange={confirmModal.onOpenChange}
        backdrop="blur"
        footer={
          <>
            <LightButton className="w-[140px]" onPress={confirmModal.onClose} >{t('common.cancel')}</LightButton>
            <DarkButton className="w-[140px]" onPress={onClickOpen}>{t('pricing.goHandleIt')}</DarkButton>
          </>
        } />

      <PayMethodModal
        isOpen={payMethodModal.isOpen}
        onOpenChange={payMethodModal.onOpenChange}
        isPaypalLoading={paypalSubscribeMuta.isPending}
        isPayssionLoading={payssionSubscribeMuta.isPending}
        onPressPaypal={onPressPaypal}
        onPressPayssion={onPressPayssion}
      />

      <UpgradeMembershipModal
        isOpen={upgradeModal.isOpen}
        onOpenChange={upgradeModal.onOpenChange}
        isLoading={paypalSubscribeMuta.isPending}
        onConfirm={onConfirmUpgrade}
      />

    </>

  )
}
